import React, { createContext, useContext, useEffect, useId, useRef, useState } from 'react';
import { Braces, Check, Copy, Plus, X } from 'lucide-react';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import NumberInput from 'shared/components/ui/NumberInput';
import { Switch } from 'features/settings/components/SettingsRows';

/**
 * The field kit for node settings. Every control is h-8 with 13px text, a
 * default border that strengthens on focus, and a transparent fill, the same
 * grammar as the Settings pages. Labels are muted 12px above the control (a
 * 400px panel has no room for a label column).
 *
 * Colour is tokens only. An invalid control swaps its border to the error
 * token and the message sits under it in `text-danger`.
 */

export const cx = (...parts) => parts.filter(Boolean).join(' ');

const FOCUS = 'focus:border-[color:var(--border-strong)] focus:outline-none';
export const inputCls = `h-8 w-full rounded-md border border-[color:var(--border-default)] bg-transparent px-2 text-[13px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-disabled)] disabled:opacity-60 ${FOCUS}`;
export const textareaCls = `min-h-[72px] w-full resize-y rounded-md border border-[color:var(--border-default)] bg-transparent px-2 py-1.5 text-[13px] leading-relaxed text-[color:var(--text-primary)] placeholder:text-[color:var(--text-disabled)] disabled:opacity-60 ${FOCUS}`;
export const monoCls = 'font-mono text-[12px]';
export const invalidCls = '!border-[color:var(--feedback-error)]';
export const ghostBtnCls = 'inline-flex h-6 items-center gap-1 rounded-md px-1.5 text-[12px] text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--surface-hover)] hover:text-[color:var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)] disabled:opacity-50';
export const removeBtnCls = 'flex h-8 w-7 shrink-0 items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--surface-hover)] hover:text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]';
export const neutralBtnCls = 'inline-flex h-8 items-center justify-center gap-1.5 rounded-md border border-[color:var(--border-default)] px-3 text-[12px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface-hover)] disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]';
export const primaryBtnCls = 'inline-flex h-8 items-center justify-center gap-1.5 rounded-md bg-brand px-3 text-[12px] font-medium text-ink-onBrand transition-colors hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]';

/* ── context: what the form knows about the node it edits ───────────────── */

/**
 * `issues`    { [fieldKey]: message } from validateNode, shown under the field.
 * `variables` [{ label, expr, hint }] offered by "Insert variable".
 * `readOnly`  the form is a record (a run's settings): no write controls.
 */
export const FieldContext = createContext({ issues: {}, variables: [], readOnly: false });
export const useFieldContext = () => useContext(FieldContext);
const IdContext = createContext(undefined);
/** The id the surrounding Field gave its label, for a control that is not one of ours. */
export const useFieldId = () => useContext(IdContext);

/* ── expression drag and drop ───────────────────────────────────────────── */

// The Input pane drags an expression token (`{{ $json.user.email }}`) that
// text, code and expression fields accept and insert at the caret.
export const EXPR_MIME = 'application/x-trodo-expr';

export function exprDropHandlers(value, onChange) {
  return {
    onDragOver: (e) => {
      if (e.dataTransfer?.types?.includes(EXPR_MIME)) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
      }
    },
    onDrop: (e) => {
      const expr = e.dataTransfer.getData(EXPR_MIME);
      if (!expr) return;
      e.preventDefault();
      const el = e.currentTarget;
      const v = String(value ?? '');
      let start = el.selectionStart;
      let end = el.selectionEnd;
      if (start == null) { start = v.length; end = v.length; }
      onChange(v.slice(0, start) + expr + v.slice(end));
      requestAnimationFrame(() => {
        try { el.focus(); el.setSelectionRange(start + expr.length, start + expr.length); } catch (_) { /* detached */ }
      });
    },
  };
}

/* ── labels, help, errors ───────────────────────────────────────────────── */

export function FieldError({ children, id }) {
  if (!children) return null;
  return <p id={id} role="alert" className="mt-1 text-[11px] leading-snug text-danger">{children}</p>;
}

export function Help({ children }) {
  if (!children) return null;
  return <p className="mt-1 text-[11px] leading-snug text-[color:var(--text-muted)]">{children}</p>;
}

/**
 * One labelled field. Reads its validation message from the form context by
 * `fieldKey`, so a field never has to be passed an error by hand.
 */
export function Field({ label, fieldKey, help, error, action, children, className }) {
  const id = useId();
  const { issues } = useFieldContext();
  const message = error || (fieldKey ? issues[fieldKey] : null);
  return (
    <div className={className}>
      {(label || action) ? (
        <div className="mb-1 flex min-h-[18px] items-center justify-between gap-2">
          {label ? <label htmlFor={id} className="text-[12px] text-[color:var(--text-muted)]">{label}</label> : <span />}
          {action}
        </div>
      ) : null}
      <IdContext.Provider value={id}>{children}</IdContext.Provider>
      {message ? <FieldError>{message}</FieldError> : <Help>{help}</Help>}
    </div>
  );
}

/** The message a field has waiting, for controls that style themselves. */
export function useIssue(fieldKey) {
  const { issues } = useFieldContext();
  return fieldKey ? issues[fieldKey] || null : null;
}

/* ── single controls ────────────────────────────────────────────────────── */

export function TextInput({ value, onChange, invalid, mono, drop = true, className, ...rest }) {
  const id = useContext(IdContext);
  return (
    <input
      id={id}
      type="text"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      aria-invalid={invalid || undefined}
      className={cx(inputCls, mono && monoCls, invalid && invalidCls, className)}
      {...(drop ? exprDropHandlers(value, onChange) : {})}
      {...rest}
    />
  );
}

export function SecretInput({ value, onChange, invalid, className, ...rest }) {
  const id = useContext(IdContext);
  return (
    <input
      id={id}
      type="password"
      autoComplete="new-password"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      aria-invalid={invalid || undefined}
      className={cx(inputCls, invalid && invalidCls, className)}
      {...rest}
    />
  );
}

export function NumberField({ value, onChange, invalid, className, ...rest }) {
  const id = useContext(IdContext);
  return (
    <NumberInput
      id={id}
      value={value}
      onChange={onChange}
      aria-invalid={invalid || undefined}
      className={cx(inputCls, invalid && invalidCls, className)}
      {...rest}
    />
  );
}

/** A textarea. `grow` makes it one line that lengthens with its content. */
export const TextArea = React.forwardRef(function TextArea({ value, onChange, invalid, mono, grow, rows, className, drop = true, ...rest }, forwardedRef) {
  const id = useContext(IdContext);
  const inner = useRef(null);
  const ref = forwardedRef || inner;
  const fit = () => {
    const el = ref.current;
    if (!grow || !el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.max(el.scrollHeight, 32)}px`;
  };
  useEffect(fit); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <textarea
      id={id}
      ref={ref}
      rows={grow ? 1 : rows}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      onInput={fit}
      aria-invalid={invalid || undefined}
      className={cx(
        grow ? `${inputCls} h-auto min-h-[32px] resize-none overflow-hidden py-[5px] leading-snug` : textareaCls,
        mono && monoCls,
        invalid && invalidCls,
        className,
      )}
      {...(drop ? exprDropHandlers(value, onChange) : {})}
      {...rest}
    />
  );
});

/** A dropdown at the same height as the inputs. */
export function SelectField({ value, onChange, options, placeholder = 'Select', invalid, searchable, ariaLabel, ...rest }) {
  return (
    <BrandDropdown
      value={value ?? ''}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      searchable={searchable}
      ariaLabel={ariaLabel}
      triggerClassName={cx('!h-8 !px-2 !py-0 !bg-transparent', invalid && invalidCls)}
      {...rest}
    />
  );
}

/** A label on the left, a switch on the right. */
export function ToggleField({ label, value, onChange, help, disabled }) {
  const id = useId();
  return (
    <div>
      <div className="flex min-h-8 items-center justify-between gap-3">
        <span id={id} className="min-w-0 text-[13px] text-[color:var(--text-primary)]">{label}</span>
        <Switch checked={!!value} onChange={onChange} disabled={disabled} label={label} />
      </div>
      <Help>{help}</Help>
    </div>
  );
}

/* ── lists ──────────────────────────────────────────────────────────────── */

function AddRow({ onClick, children }) {
  return (
    <button type="button" onClick={onClick} className={ghostBtnCls}>
      <Plus className="h-3 w-3" aria-hidden /> {children}
    </button>
  );
}

/** Two inputs per row, a ghost remove, an add link. Values are plain pairs. */
export function PairList({ pairs, onChange, aKey = 'key', bKey = 'value', aPlaceholder, bPlaceholder, bMono, addLabel = 'Add' }) {
  const set = (i, patch) => onChange(pairs.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  return (
    <div className="space-y-1.5">
      {pairs.map((p, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <input
            type="text"
            aria-label={aPlaceholder}
            value={p[aKey] ?? ''}
            onChange={(e) => set(i, { [aKey]: e.target.value })}
            placeholder={aPlaceholder}
            className={cx(inputCls, 'w-[34%] shrink-0')}
          />
          <input
            type="text"
            aria-label={bPlaceholder}
            value={p[bKey] ?? ''}
            onChange={(e) => set(i, { [bKey]: e.target.value })}
            placeholder={bPlaceholder}
            spellCheck={false}
            className={cx(inputCls, 'min-w-0 flex-1', bMono && monoCls)}
            {...exprDropHandlers(p[bKey], (v) => set(i, { [bKey]: v }))}
          />
          <button type="button" onClick={() => onChange(pairs.filter((_, j) => j !== i))} aria-label="Remove row" title="Remove" className={removeBtnCls}>
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      <AddRow onClick={() => onChange([...pairs, { [aKey]: '', [bKey]: '' }])}>{addLabel}</AddRow>
    </div>
  );
}

/** A map of key to value (HTTP headers and the like). */
export function KeyValueList({ value, onChange, addLabel = 'Add row' }) {
  const entries = Object.entries(value || {});
  const pairs = entries.map(([key, v]) => ({ key, value: String(v ?? '') }));
  const emit = (next) => onChange(Object.fromEntries(next.filter((p) => p.key !== '').map((p) => [p.key, p.value])));
  // An empty key cannot live in an object, so a fresh row is held until it is named.
  const [draft, setDraft] = useState(0);
  const shown = [...pairs, ...Array.from({ length: draft }, () => ({ key: '', value: '' }))];
  return (
    <PairList
      pairs={shown}
      aPlaceholder="key"
      bPlaceholder="value"
      addLabel={addLabel}
      onChange={(next) => {
        const added = next.length > pairs.length;
        if (added) { setDraft(1); return; }
        const named = next.filter((p) => p.key !== '');
        setDraft(next.length > named.length && draft ? 1 : 0);
        emit(next);
      }}
    />
  );
}

/* ── expressions ────────────────────────────────────────────────────────── */

const BASE_VARIABLES = [
  { label: '$input', expr: '$input', hint: 'Output of the previous step' },
  { label: '$trigger', expr: '$trigger', hint: 'What started this run' },
  { label: '$now', expr: '$now', hint: 'The current time' },
  { label: '$today', expr: '$today', hint: 'Today’s date' },
];

/** "Insert variable": a small popover of what an expression can read. */
export function VariableMenu({ onInsert, disabled }) {
  const { variables } = useFieldContext();
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (!wrap.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey, true); };
  }, [open]);
  const list = [...(variables || []), ...BASE_VARIABLES];
  return (
    <div ref={wrap} className="relative">
      <button type="button" disabled={disabled} onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open} className={ghostBtnCls}>
        <Braces className="h-3 w-3" aria-hidden /> Insert variable
      </button>
      {open ? (
        <div role="menu" className="absolute right-0 z-30 mt-1 max-h-60 w-64 overflow-y-auto rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] py-1 shadow-token-lg">
          {list.map((v) => (
            <button
              key={v.expr}
              type="button"
              role="menuitem"
              onClick={() => { onInsert(`{{ ${v.expr} }}`); setOpen(false); }}
              className="block w-full px-2.5 py-1.5 text-left hover:bg-[color:var(--surface-hover)] focus-visible:bg-[color:var(--surface-hover)] focus-visible:outline-none"
            >
              <span className="block truncate font-mono text-[12px] text-[color:var(--text-primary)]">{v.label}</span>
              {v.hint ? <span className="block truncate text-[11px] text-[color:var(--text-muted)]">{v.hint}</span> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * A field that holds `{{ }}` expressions: a mono textarea that grows, with
 * "Insert variable" beside the label. Dropping a field from the Input pane
 * also works.
 */
export function ExpressionField({ label, fieldKey, value, onChange, placeholder, help, readOnly }) {
  const ref = useRef(null);
  const insert = (token) => {
    const el = ref.current;
    const v = String(value ?? '');
    const start = el?.selectionStart ?? v.length;
    const end = el?.selectionEnd ?? start;
    onChange(v.slice(0, start) + token + v.slice(end));
    requestAnimationFrame(() => {
      try { el?.focus(); el?.setSelectionRange(start + token.length, start + token.length); } catch (_) { /* detached */ }
    });
  };
  const message = useIssue(fieldKey);
  return (
    <Field
      label={label}
      fieldKey={fieldKey}
      help={help}
      action={readOnly ? null : <VariableMenu onInsert={insert} />}
    >
      <TextArea ref={ref} grow mono value={value} onChange={onChange} placeholder={placeholder} spellCheck={false} invalid={!!message} />
    </Field>
  );
}

/* ── copy ───────────────────────────────────────────────────────────────── */

export async function copyText(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const el = document.createElement('textarea');
  el.value = text;
  el.setAttribute('readonly', '');
  el.style.position = 'fixed';
  el.style.left = '-9999px';
  document.body.appendChild(el);
  el.select();
  try { document.execCommand('copy'); } finally { document.body.removeChild(el); }
}

/** An icon button that copies `text` and shows a check for a moment. */
export function CopyButton({ text, label = 'Copy', className, disabled }) {
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!done) return undefined;
    const t = setTimeout(() => setDone(false), 1400);
    return () => clearTimeout(t);
  }, [done]);
  return (
    <button
      type="button"
      disabled={disabled}
      aria-label={done ? 'Copied' : label}
      title={done ? 'Copied' : label}
      onClick={async () => { try { await copyText(text); setDone(true); } catch (_) { setDone(false); } }}
      className={cx('flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[color:var(--border-default)] text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--surface-hover)] hover:text-[color:var(--text-primary)] disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]', className)}
    >
      {done ? <Check className="h-3.5 w-3.5 text-[color:var(--feedback-success)]" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

/* ── notices ────────────────────────────────────────────────────────────── */

/** An inline note: neutral by default, warning or error by tone. */
export function Notice({ tone = 'neutral', children, className }) {
  const toneCls = tone === 'error'
    ? 'border-[color:var(--feedback-error)] bg-danger-soft text-danger'
    : tone === 'warning'
      ? 'border-[color:var(--feedback-warning)] bg-[color:var(--feedback-warning-bg)] text-[color:var(--text-primary)]'
      : 'border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] text-[color:var(--text-muted)]';
  return <div className={cx('rounded-md border px-2.5 py-2 text-[12px] leading-snug', toneCls, className)}>{children}</div>;
}

/** A dashed empty box ("Select a model provider first."). */
export function EmptyHint({ children }) {
  return (
    <div className="flex min-h-8 items-center rounded-md border border-dashed border-[color:var(--border-default)] px-2.5 py-1.5 text-[12px] text-[color:var(--text-muted)]">
      {children}
    </div>
  );
}
